import { Injectable } from '@angular/core';

import { AbstractSecurityStorage } from 'angular-auth-oidc-client';

/**
 * The OIDC entries that must survive the top-level authorization redirect
 * (AITH-121). Sign-in is a redirect rather than a popup because Safari refuses
 * a window opened after the library's asynchronous discovery request, and the
 * redirect destroys the page holding the in-memory map — so the callback would
 * have no verifier or state to validate against and every sign-in would fail.
 *
 * These four are protocol transaction state, not credentials: the PKCE
 * verifier, the CSRF state, the replay nonce, and the in-progress marker. They
 * are single-use, expire with the authorization request, and are worthless
 * without the authorization code that arrives in the URL. Session storage is
 * also where the library's own default keeps them.
 *
 * Everything else — `authnResult`, `authzData`, `reusable_refresh_token`,
 * `userData` — is a credential or profile datum and stays memory-only.
 */
const TRANSACTION_KEYS: readonly string[] = [
  'codeVerifier',
  'authStateControl',
  'authNonce',
  'storageCodeFlowInProgress',
];

/**
 * The provider's discovery document, mirrored alongside a live transaction
 * only. Redeeming the authorization code reads the token endpoint straight
 * back out of storage (`codeFlowCodeRequest`), and the fresh document has not
 * re-run discovery by the time the callback is processed. It is public
 * metadata — every value in it is served unauthenticated from the issuer.
 */
const DISCOVERY_KEY = 'authWellKnownEndPoints';

/** Namespace for the redirect-surviving mirror. Nothing else may use it. */
const MIRROR_PREFIX = 'aith.auth.redirect.';

/**
 * Credential storage (FR-015, R-103): a root-scoped `Map` — no token, refresh
 * token, or user profile ever reaches `localStorage` or `sessionStorage`. The
 * only persisted `localStorage` key in the app remains
 * `aith.ui.sidebar.collapsed`.
 *
 * The one exception is the redirect-surviving mirror described on
 * {@link TRANSACTION_KEYS}: `sessionStorage` (tab-scoped, cleared with the
 * tab), dropped again the moment the flow that needs it completes — and, via
 * {@link InMemorySecurityStorage.discardRedirectMirror}, when an attempt ends
 * without completing at all (provider error, cancellation, abandonment).
 *
 * Consequences (accepted in R-103): a full page reload loses the in-memory
 * session; each tab authenticates independently.
 *
 * The OIDC library stores every value of one named configuration under its
 * configId key, so per-configuration clearing (the not-provisioned and
 * most-recent-session-wins paths) is removing that single key.
 */
@Injectable({ providedIn: 'root' })
export class InMemorySecurityStorage extends AbstractSecurityStorage {
  private readonly entries = new Map<string, string>();

  /** Whether the tab-scoped redirect mirror is available for a new attempt. */
  canUseRedirectMirror(): boolean {
    try {
      sessionStorage.getItem(MIRROR_PREFIX);
      return true;
    } catch {
      return false;
    }
  }

  override read(key: string): string | null {
    const inMemory = this.entries.get(key);
    if (inMemory !== undefined) return inMemory;
    // Only reachable on the first read after the redirect, when the map that
    // held this configuration died with the previous document.
    try {
      return sessionStorage.getItem(MIRROR_PREFIX + key);
    } catch {
      return null;
    }
  }

  override write(key: string, value: string): void {
    this.entries.set(key, value);
    const transactionState = transactionSubset(value);
    try {
      if (transactionState === null) sessionStorage.removeItem(MIRROR_PREFIX + key);
      else sessionStorage.setItem(MIRROR_PREFIX + key, transactionState);
    } catch {
      // Credentials remain in memory; AuthFlowService refuses redirect sign-in.
    }
  }

  /**
   * Writes an app-level entry that must survive the redirect verbatim (the
   * sign-in returnUrl and the in-flight issuer category). Neither is a
   * credential.
   */
  writeAcrossRedirect(key: string, value: string): boolean {
    this.entries.set(key, value);
    try {
      sessionStorage.setItem(MIRROR_PREFIX + key, value);
      return true;
    } catch {
      // Credentials remain in memory; AuthFlowService refuses redirect sign-in.
      return false;
    }
  }

  override remove(key: string): void {
    this.entries.delete(key);
    try {
      sessionStorage.removeItem(MIRROR_PREFIX + key);
    } catch {
      // Best-effort cleanup only when browser storage is unavailable.
    }
  }

  override clear(): void {
    this.entries.clear();
    for (const key of mirrorKeys()) this.removeMirrorKey(key);
  }

  /** Clears only the entries of one named OIDC configuration. */
  clearConfiguration(configId: string): void {
    this.remove(configId);
  }

  /**
   * Drops the whole redirect mirror while leaving in-memory state alone.
   *
   * The mirror exists to carry ONE authorization across ONE redirect. An
   * attempt that fails at the provider, is cancelled, or is simply abandoned
   * never reaches the completion path that blanks it, so without this the
   * verifier, state and nonce would stay readable in `sessionStorage` for the
   * rest of the tab's life — the leak AITH-121's review identified, and the
   * only way the narrowed FR-015 exception stays "attempt-local" in fact and
   * not just in wording.
   *
   * Memory is deliberately untouched: a session established through the *other*
   * configuration never shared this mirror and must survive a failed attempt.
   */
  discardRedirectMirror(): void {
    for (const key of mirrorKeys()) this.removeMirrorKey(key);
  }

  private removeMirrorKey(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Best-effort cleanup only when browser storage is unavailable.
    }
  }
}

/**
 * The mirror-worthy part of one configuration's stored blob, or null when it
 * holds none — which is also how a completed flow clears its own mirror, since
 * the library blanks each of these on success.
 */
function transactionSubset(value: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const blob = parsed as Record<string, unknown>;
  const subset: Record<string, unknown> = {};
  for (const key of TRANSACTION_KEYS) {
    // Falsy covers every "not in flight" spelling the library uses: absent,
    // null, '' (state) and false (the in-progress marker).
    if (blob[key]) subset[key] = blob[key];
  }
  if (Object.keys(subset).length === 0) return null;
  if (blob[DISCOVERY_KEY]) subset[DISCOVERY_KEY] = blob[DISCOVERY_KEY];
  return JSON.stringify(subset);
}

function mirrorKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key !== null && key.startsWith(MIRROR_PREFIX)) keys.push(key);
    }
  } catch {
    return [];
  }
  return keys;
}
